import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClockRotateLeft,
  faWandMagicSparkles,
  faUserPen,
  faChevronDown,
  faChevronUp,
  faSpinner,
  faPenToSquare,
  faCirclePlus,
  faCircleMinus,
  faEyeSlash,
  faEye,
  faLayerGroup,
} from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";
import ModalHeader from "./ModalHeader";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";

const DAY_NAMES = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

const DIFF_COLORS = {
  added:     { bg: "rgba(74,222,128,0.12)",   border: "rgba(74,222,128,0.30)",   text: "var(--feedback-success)"  },
  removed:   { bg: "rgba(248,113,113,0.12)",  border: "rgba(248,113,113,0.30)",  text: "var(--feedback-error)"  },
  changed:   { bg: "rgba(99,179,237,0.12)",   border: "rgba(99,179,237,0.30)",   text: "#63b3ed"  },
  unchanged: { bg: "transparent",             border: "transparent",             text: "var(--text-primary)"  },
};

function formatDate(isoStr) {
  if (!isoStr) return "-";
  const d = new Date(isoStr);
  return d.toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

/** Calcula diff entre dois arrays de itens de plano. */
function computePlanDiff(beforeItems = [], afterItems = []) {
  const beforeMap = {};
  beforeItems.forEach(it => { if (it.exercise) beforeMap[it.exercise] = it; });

  const result = [];
  const seen = new Set();

  // Pass 1: items in "after"
  for (const item of afterItems) {
    const ex = item.exercise;
    const before = beforeMap[ex];
    if (!before) {
      result.push({ item, status: "added", before: null });
    } else {
      const sameVals =
        String(before.sets ?? "") === String(item.sets ?? "") &&
        String(before.reps ?? "") === String(item.reps ?? "") &&
        String(before.rest_seconds ?? "") === String(item.rest_seconds ?? "");
      result.push({ item, status: sameVals ? "unchanged" : "changed", before: sameVals ? null : before });
    }
    if (ex) seen.add(ex);
  }

  // Pass 2: items only in "before" (removed)
  for (const item of beforeItems) {
    if (item.exercise && !seen.has(item.exercise)) {
      result.push({ item, status: "removed", before: null });
    }
  }

  return result;
}

/** Célula de valor com "era: X" quando mudou. */
function DiffCell({ value, oldValue, align = "center" }) {
  const changed = oldValue !== null && oldValue !== undefined && String(oldValue) !== String(value ?? "");
  return (
    <td style={{ textAlign: align, padding: "5px 4px", verticalAlign: "top" }}>
      <span style={{ color: "var(--text-primary)", fontWeight: changed ? 600 : 400 }}>{value ?? "-"}</span>
      {changed && (
        <div style={{ color: "var(--text-0-40)", fontSize: 10, textDecoration: "line-through", lineHeight: 1.3 }}>
          {oldValue ?? "-"}
        </div>
      )}
    </td>
  );
}

/** Vista diff de um único par before-plan / after-plan. */
function PlanDiff({ plan, beforePlan, open: initialOpen = false }) {
  useBodyScrollLock();
  const [open, setOpen] = useState(initialOpen);
  const diffRows = computePlanDiff(beforePlan?.items, plan?.items);
  const hasDiff = diffRows.some(r => r.status !== "unchanged");
  const changedCount = diffRows.filter(r => r.status !== "unchanged").length;

  return (
    <div style={{ marginBottom: 2 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          background: "none", border: "none", padding: "2px 0", cursor: "pointer",
          color: "var(--text-0-60)", fontSize: 12, display: "flex",
          alignItems: "center", gap: 6, width: "100%",
        }}
      >
        <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} style={{ fontSize: 10, flexShrink: 0 }} />
        <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>{plan?.name || beforePlan?.name || "Sem nome"}</span>
        {plan?.day_of_week != null && (
          <span style={{ background: "rgba(74,222,128,0.12)", color: "var(--feedback-success)", borderRadius: 4, padding: "1px 6px", fontSize: 10 }}>
            {DAY_NAMES[plan.day_of_week]}
          </span>
        )}
        {hasDiff && (
          <span style={{ marginLeft: "auto", fontSize: 10, color: "var(--text-0-40)" }}>
            {changedCount} alteraç{changedCount !== 1 ? "ões" : "ão"}
          </span>
        )}
      </button>

      {open && (
        <div style={{ marginTop: 6, borderRadius: 8, overflow: "hidden", border: "1px solid var(--border-0-08)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "rgba(255,255,255,0.04)" }}>
                <th style={{ textAlign: "left", padding: "5px 8px", fontWeight: 500, color: "var(--text-0-40)", fontSize: 11 }}>Exercício</th>
                <th style={{ textAlign: "center", padding: "5px 4px", fontWeight: 500, color: "var(--text-0-40)", fontSize: 11, width: 44 }}>Séries</th>
                <th style={{ textAlign: "center", padding: "5px 4px", fontWeight: 500, color: "var(--text-0-40)", fontSize: 11 }}>Reps</th>
                <th style={{ textAlign: "center", padding: "5px 4px", fontWeight: 500, color: "var(--text-0-40)", fontSize: 11 }}>Desc (s)</th>
              </tr>
            </thead>
            <tbody>
              {diffRows.map((row, i) => {
                const c = DIFF_COLORS[row.status];
                const isRemoved = row.status === "removed";
                return (
                  <tr key={i} style={{
                    background: c.bg,
                    borderLeft: `3px solid ${row.status !== "unchanged" ? c.border : "transparent"}`,
                  }}>
                    <td style={{
                      padding: "5px 8px", color: c.text,
                      textDecoration: isRemoved ? "line-through" : "none",
                      opacity: isRemoved ? 0.7 : 1,
                    }}>
                      {row.item.exercise || "-"}
                    </td>
                    <DiffCell
                      value={isRemoved ? row.item.sets : (row.item.sets ?? "-")}
                      oldValue={row.before ? row.before.sets : null}
                    />
                    <DiffCell
                      value={isRemoved ? row.item.reps : (row.item.reps || "-")}
                      oldValue={row.before ? row.before.reps : null}
                    />
                    <DiffCell
                      value={isRemoved ? row.item.rest_seconds : (row.item.rest_seconds || "-")}
                      oldValue={row.before ? row.before.rest_seconds : null}
                    />
                  </tr>
                );
              })}
            </tbody>
          </table>
          {hasDiff && (
            <div style={{ display: "flex", gap: 12, padding: "6px 10px", background: "var(--border-0-15)", fontSize: 10, color: "var(--text-0-40)" }}>
              <span style={{ color: DIFF_COLORS.added.text }}>■ adicionado</span>
              <span style={{ color: DIFF_COLORS.removed.text }}>■ removido</span>
              <span style={{ color: DIFF_COLORS.changed.text }}>■ alterado</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Agrupa planos before/after em pares pelo plan_id ou índice. */
function pairPlans(beforePlans, afterPlans) {
  const allIds = new Set([
    ...beforePlans.map(p => p.plan_id),
    ...afterPlans.map(p => p.plan_id),
  ]);
  const beforeById = Object.fromEntries(beforePlans.map(p => [p.plan_id, p]));
  const afterById  = Object.fromEntries(afterPlans.map(p  => [p.plan_id, p]));

  return [...allIds].map(id => ({
    plan:       afterById[id]  || null,
    beforePlan: beforeById[id] || null,
  }));
}

const CHANGE_TYPE_META = {
  ai_generation:       { label: "Substituição por IA",      icon: faWandMagicSparkles, color: "var(--text-primary)", bg: "rgba(167,139,250,0.06)", border: "rgba(167,139,250,0.20)" },
  plan_added:          { label: "Treino adicionado",        icon: faUserPen,           color: "var(--feedback-success)", bg: "rgba(74,222,128,0.06)",  border: "rgba(74,222,128,0.20)"  },
  plan_removed:        { label: "Treino removido",          icon: faUserPen,           color: "var(--feedback-error)", bg: "rgba(248,113,113,0.06)", border: "rgba(248,113,113,0.20)" },
  exercicio_editado:   { label: "Exercício editado",        icon: faPenToSquare,       color: "var(--input-border-focus)", bg: "rgba(99,179,237,0.06)",  border: "rgba(99,179,237,0.20)"  },
  exercicio_adicionado:{ label: "Exercício adicionado",     icon: faCirclePlus,        color: "var(--feedback-success)", bg: "rgba(74,222,128,0.06)",  border: "rgba(74,222,128,0.20)"  },
  exercicio_removido:  { label: "Exercício removido",       icon: faCircleMinus,       color: "var(--feedback-error)", bg: "rgba(248,113,113,0.06)", border: "rgba(248,113,113,0.20)" },
  exercicio_ocultado:  { label: "Exercício ocultado",       icon: faEyeSlash,          color: "var(--feedback-warning)", bg: "rgba(251,146,60,0.06)",  border: "rgba(251,146,60,0.20)"  },
  exercicio_restaurado:{ label: "Exercício restaurado",     icon: faEye,               color: "var(--feedback-success)", bg: "rgba(52,211,153,0.06)",  border: "rgba(52,211,153,0.20)"  },
  plano_base_editado:  { label: "Plano base editado",       icon: faLayerGroup,        color: "var(--feedback-warning)", bg: "rgba(251,191,36,0.06)",  border: "rgba(251,191,36,0.20)"  },
  edicao_manual:       { label: "Edição manual",            icon: faPenToSquare,       color: "var(--input-border-focus)", bg: "rgba(99,179,237,0.06)",  border: "rgba(99,179,237,0.20)"  },
};

function ChangeLogEntry({ entry }) {
  const meta = CHANGE_TYPE_META[entry.change_type] || CHANGE_TYPE_META.plan_added;
  const [open, setOpen] = useState(false);

  const beforePlans = entry.before_snapshot
    ? (Array.isArray(entry.before_snapshot) ? entry.before_snapshot : [entry.before_snapshot])
    : [];
  const afterPlans = entry.after_snapshot
    ? (Array.isArray(entry.after_snapshot) ? entry.after_snapshot : [entry.after_snapshot])
    : [];

  const pairs = pairPlans(beforePlans, afterPlans);
  const hasPairs = pairs.length > 0;

  return (
    <div style={{
      background: meta.bg,
      border: `1px solid ${meta.border}`,
      borderRadius: 10,
      padding: "12px 14px",
    }}>
      {/* Header da entrada */}
      <div
        style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: hasPairs ? "pointer" : "default" }}
        onClick={() => hasPairs && setOpen(o => !o)}
      >
        <div style={{
          width: 32, height: 32, borderRadius: 8, flexShrink: 0,
          background: `${meta.color}18`,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: meta.color, fontSize: 14,
        }}>
          <FontAwesomeIcon icon={meta.icon} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ color: meta.color, fontWeight: 600, fontSize: 13 }}>
              {meta.label}
            </span>
            {entry.plan_name ? (
              <span style={{
                color: "var(--text-0-50)", fontSize: 11,
                background: "var(--border-0-04)", borderRadius: 4, padding: "1px 7px",
              }}>
                {entry.plan_name}
              </span>
            ) : pairs.length > 1 && (
              <span style={{
                color: "var(--text-0-40)", fontSize: 11,
                background: "rgba(255,255,255,0.04)", borderRadius: 4, padding: "1px 7px",
              }}>
                {pairs.length} treinos
              </span>
            )}
          </div>
          <div style={{ color: "var(--text-0-40)", fontSize: 11, marginTop: 2 }}>
            {formatDate(entry.changed_at)}
          </div>
        </div>
        {hasPairs && (
          <FontAwesomeIcon
            icon={open ? faChevronUp : faChevronDown}
            style={{ fontSize: 12, color: "var(--text-0-40)", flexShrink: 0, marginTop: 6 }}
          />
        )}
      </div>

      {/* Diff por plano */}
      {open && hasPairs && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
          {pairs.map((pair, i) => (
            <PlanDiff
              key={i}
              plan={pair.plan}
              beforePlan={pair.beforePlan}
              open={pairs.length === 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function WorkoutHistoryModal({ open, onClose, studentId, studentName }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !studentId) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/workout-change-logs/${studentId}`)
      .then(res => { if (!cancelled) setLogs(res.data || []); })
      .catch(() => { if (!cancelled) setLogs([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, studentId]);

  if (!open) return null;

  return createPortal(
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 3000,
        background: "var(--border-0-65)", backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "16px",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "var(--surface-primary)", borderRadius: 16,
          border: "1px solid var(--border-0-10)",
          width: "100%", maxWidth: 560,
          maxHeight: "82vh", display: "flex", flexDirection: "column",
          boxShadow: "0 24px 64px rgba(0,0,0,0.6)",
        }}
        onClick={e => e.stopPropagation()}
      >
        <ModalHeader onClose={onClose} />
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", gap: 12,
          padding: "14px 20px 12px",
          borderBottom: "1px solid var(--border-0-08)",
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: "rgba(74,222,128,0.10)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "var(--feedback-success)", fontSize: 16, flexShrink: 0,
          }}>
            <FontAwesomeIcon icon={faClockRotateLeft} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ color: "var(--text-primary)", fontWeight: 700, fontSize: 15 }}>Histórico de Treinos</div>
            {studentName && (
              <div style={{ color: "var(--text-0-50)", fontSize: 12 }}>{studentName}</div>
            )}
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: "40px 0", color: "var(--text-0-40)" }}>
              <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: 24, marginBottom: 10 }} />
              <div style={{ fontSize: 13 }}>Carregando histórico...</div>
            </div>
          ) : logs.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 0", color: "var(--text-0-40)", fontSize: 13 }}>
              Nenhuma alteração registrada ainda.
            </div>
          ) : (
            logs.map(entry => (
              <ChangeLogEntry key={entry.id} entry={entry} />
            ))
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
